import { useEffect, useRef, type JSX } from 'react';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInfo } from '@c2fo/liquidity';
import plane from '@/enterprise/assets/plane.svg?url';
import { MONDU_HANDOFF_MODAL_CONTENT } from '@/enterprise/features/extendedNetworkFinance/content/sharedContent';
import useMonduHandoffRedirect, {
  type ConsentPath,
} from '@/enterprise/features/extendedNetworkFinance/useMonduHandoffRedirect';
import { useReporting } from '@/reporting';
import { isImpersonationSession } from '@/utils/token';
import { useAgreementsUrl } from '@/utils/useAgreementsUrl';

interface MonduHandoffModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * The supplier's standing, reported on every event this modal emits and passed to the handoff.
   * Supplied by the page rather than assumed to be `modal`: a `prior-consent` supplier whose skip
   * path failed is dropped into this modal, and pressing Proceed there is a retry, not a new
   * consent. Assuming `modal` would count those retries as consents and mislabel their handoff.
   */
  consentPath: ConsentPath;
  /** Shows the request-error Alert on open, for a handoff that already failed outside the modal. */
  showRequestError?: boolean;
}

/**
 * Interstitial shown before handing a supplier off to Mondu, disclosing the third-party
 * destination and the data C2FO shares to support the application.
 */
const MonduHandoffModal = ({
  open,
  onClose,
  consentPath,
  showRequestError = false,
}: MonduHandoffModalProps): JSX.Element => {
  const { privacyLink } = useAgreementsUrl();
  const isImpersonating = isImpersonationSession();
  const { runHandoff, isBusy, hasError, clearError } = useMonduHandoffRedirect();
  const { trackEnterpriseEvent } = useReporting();
  const showError = hasError || showRequestError;
  // The page renders this modal unconditionally, so it never unmounts between opens. Both refs are
  // keyed to one open rather than one mount: without that, `shown` would report once per page visit
  // and `accepted` would report once ever.
  const hasReportedOpenRef = useRef(false);
  const hasReportedAcceptRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasReportedOpenRef.current = false;
      return;
    }

    if (hasReportedOpenRef.current) {
      return;
    }

    hasReportedOpenRef.current = true;
    hasReportedAcceptRef.current = false;
    void trackEnterpriseEvent('extended-network-marketing::consent-modal::shown', { consentPath });
  }, [open, consentPath, trackEnterpriseEvent]);

  const closeModal = (): void => {
    if (isBusy) {
      return;
    }

    // Reported before `onClose` so the accept flag for this open is still readable.
    void trackEnterpriseEvent('extended-network-marketing::consent-modal::dismissed', {
      attemptedAccept: hasReportedAcceptRef.current,
      consentPath,
    });
    clearError();
    onClose();
  };

  const handleProceed = async (): Promise<void> => {
    if (isImpersonating) {
      return;
    }

    // Retry after a failed handoff presses Proceed again, which is one consent, not two.
    if (!hasReportedAcceptRef.current) {
      hasReportedAcceptRef.current = true;
      void trackEnterpriseEvent('extended-network-marketing::consent-modal::accepted', { consentPath });
    }

    await runHandoff({ consentPath });
  };

  return (
    <Modal open={open} onClose={closeModal} size="sm" disableOutsideClickClose={isBusy}>
      <ModalTitleInfo>{MONDU_HANDOFF_MODAL_CONTENT.title}</ModalTitleInfo>
      <ModalContent center>
        <img src={plane} alt="" className="size-[70px]" />
        <p>{MONDU_HANDOFF_MODAL_CONTENT.disclaimer}</p>
        <p>
          {MONDU_HANDOFF_MODAL_CONTENT.consent.beforeLink}
          <a href={privacyLink} target="_blank" rel="noreferrer" className="font-semibold text-blue-500">
            {MONDU_HANDOFF_MODAL_CONTENT.consent.privacyPolicyLabel}
          </a>
          {MONDU_HANDOFF_MODAL_CONTENT.consent.afterLink}
        </p>
        <p className="italic">{MONDU_HANDOFF_MODAL_CONTENT.redirectNote}</p>
        {/* ModalContent center applies text-center, which the Alert description inherits while its
            title stays left of the icon. Opt these back out to keep the Alert internally aligned. */}
        {isImpersonating && (
          <div className="w-full text-left">
            <Alert type="info" full description={MONDU_HANDOFF_MODAL_CONTENT.impersonationMessage} />
          </div>
        )}
        {showError && (
          <div className="w-full text-left">
            <Alert
              type="error"
              full
              title={MONDU_HANDOFF_MODAL_CONTENT.requestError.title}
              description={MONDU_HANDOFF_MODAL_CONTENT.requestError.description}
            />
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={closeModal} disabled={isBusy}>
          {MONDU_HANDOFF_MODAL_CONTENT.cancelLabel}
        </Button>
        <Button variant="cta" onClick={handleProceed} loading={isBusy} disabled={isImpersonating || isBusy}>
          {MONDU_HANDOFF_MODAL_CONTENT.proceedLabel}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default MonduHandoffModal;
